import { DayTimeSelectorDrawer } from '../DayTimeSelectorDrawer'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { TimeOfDay, CustomScheduleConfig, CustomTime, formatTimeSelection } from '../types'
import { Button } from 'blaze-ui/Button'
import { ArrowRight } from 'blaze-ui/icons'
import { ChevronRightSmall } from 'blaze-ui/icons/24'
import { MobileWizard } from 'components/Wizards/MobileWizards/Layout'
import { Field as BaseField } from 'components/Wizards/MobileWizards/Field/Field'
import { Chip } from 'components/Chip/Chip'
import Styles from './DaySelector.module.scss'

interface CustomScheduleProps {
  value: CustomScheduleConfig
  onChange: (value: CustomScheduleConfig) => void
  onComplete: () => void
  onBack: () => void
}

export const CustomSchedule = observer(({ value, onChange, onComplete, onBack }: CustomScheduleProps) => {
  const { drawersStore } = useRootStore()

  const handleDayToggle = (day: string) => {
    const newSelectedDays = value.selectedDays.includes(day)
      ? value.selectedDays.filter((d) => d !== day)
      : [...value.selectedDays, day]

    onChange({
      ...value,
      selectedDays: newSelectedDays,
    })
  }

  const handleEditDayTime = (day: string) => {
    drawersStore.openDrawer(DayTimeSelectorDrawer, {
      selectedDay: day,
      currentSelection: value.dayTimeSelections[day]?.timeType || TimeOfDay.Morning,
      currentCustomTime: value.dayTimeSelections[day]?.customTime,
      onSave: (timeType: TimeOfDay, customTime?: CustomTime) => handleDayTimeSelectionSave(day, timeType, customTime),
      onBack: () => {
        drawersStore.closeDrawer()
      },
    })
  }

  const handleDayTimeSelectionSave = (day: string, timeType: TimeOfDay, customTime?: CustomTime) => {
    onChange({
      ...value,
      dayTimeSelections: {
        ...value.dayTimeSelections,
        [day]: { day, timeType, customTime },
      },
    })
    drawersStore.closeDrawer()
  }

  const formatDayDisplay = (day: string) => {
    return `Every ${day}`
  }

  const getTimeDisplayForDay = (day: string): string => {
    const selection = value.dayTimeSelections[day]
    if (!selection) return 'In the morning'
    return formatTimeSelection(selection)
  }

  const dayOptions = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

  const renderDayTimeButtons = () => {
    if (!value.selectedDays?.length) return null

    return (
      <BaseField label="Choose post times for each day">
        <div className={Styles.dayTimeContainer}>
          {value.selectedDays.map((day) => (
            <div key={day} className={Styles.dayTimeButton} onClick={() => handleEditDayTime(day)}>
              <div className={Styles.dayName}>{formatDayDisplay(day)}</div>
              <div className={Styles.timeInfo}>
                <span className={Styles.timeLabel}>{getTimeDisplayForDay(day)}</span>
                <ChevronRightSmall />
              </div>
            </div>
          ))}
        </div>
      </BaseField>
    )
  }

  return (
    <>
      <MobileWizard.Content>
        <MobileWizard.Section>
          <MobileWizard.Title>Which days would you like to post?</MobileWizard.Title>
        </MobileWizard.Section>

        <BaseField label="Select days of the week">
          <div className={Styles.dayChipsContainer}>
            {dayOptions.map((day) => (
              <Chip
                key={day}
                variant="catalogue"
                selectable={true}
                deletable={false}
                isSelected={value.selectedDays.includes(day)}
                onPress={() => handleDayToggle(day)}
              >
                {day}
              </Chip>
            ))}
          </div>
        </BaseField>

        {value.selectedDays?.length > 0 && renderDayTimeButtons()}
      </MobileWizard.Content>

      <MobileWizard.ButtonFooter>
        <Button
          size="lg"
          variant="primary"
          endIcon={ArrowRight}
          fullWidth
          onPress={onComplete}
          isDisabled={!value.selectedDays?.length}
        >
          {value.selectedDays?.length > 0 ? 'Continue' : 'Select Days'}
        </Button>
      </MobileWizard.ButtonFooter>
    </>
  )
})
